import {Link} from "react-router-dom";
import React from "react";
import {Model, TimeDelta, Client, getCookie, isEmpty} from "weed-js";
import {
    DutchieAccount,
    Brand,
    Category,
    Customer,
    Employee, Inventory,
    Item,
    MasterCategory, Producer,
    Product, Strain,
    Transaction, Vendor,
    Package,
} from "../../../../../../models/dutchie";

import {
    MetrcAccount,
    Facility as MetrcFacility,
    ProductCategoryType as MetrcProductCategoryType,
    ProductCategory as MetrcProductCategory,
    Product as MetrcProduct,
    LocationType as MetrcLocationType,
    Location as MetrcLocation,
    Package as MetrcPackage,
    Employee as MetrcEmployee,
    Receipt as MetrcReceipt,
    Transaction as MetrcTransaction,
    Strain as MetrcStrain,
    Brand as MetrcBrand,
    PackageWholesalePrice as MetrcPackageWholesalePrice,
    InventoryItem as MetrcInventory,
    Item as MetrcTransactionItem,
    Tax as MetrcTax
} from "../../../../../../models/metrc";

import {
    CanonicalOrganization,
    CanonicalIntegrationAccount,
    CanonicalFacility,
    CanonicalMasterCategory,
    CanonicalCategory,
    CanonicalBrand,
    CanonicalVendor,
    CanonicalStrain,
    CanonicalProducer,
    CanonicalProduct,
    CanonicalCustomer,
    CanonicalEmployee,
    CanonicalTransaction,
    CanonicalPackage,
    CanonicalTransactionItem,
    CanonicalInventory
} from "../../../../../../models/canonical";
import dayjs from "dayjs";


export const MAX_DAYS = 14;
export const MIN_DAYS = 7;


export const uniqueById = (arr) => {
    const seen = new Set();
    return arr.filter(item => {
        if (item && !seen.has(item.id)) {
            seen.add(item.id);
            return true;
        }
        return false;
    });
}

export class IntegrationUtils {
    static getFeedbackWrapper = (setInProgress) => {
        return (promise) => {
            return promise.then((input) => {
                setInProgress(prev => prev - 1);
                return input;
            }).catch(err => {
                setInProgress(prev => prev - 1);
                console.error("Error fetching data:", err);
                throw err;
            });
        }
    }
}

export class DutchieUtils extends IntegrationUtils {

    static getProductName = ({
        product,
        brand,
        vendor,
        masterCategory,
        category,
        strain,
        selectedOrganizationId,
        selectedIntegration
    }) => {
        let name = "";

        if (!isEmpty(brand)) {
            name += `${brand.name} `;
        }
        if (!isEmpty(vendor)) {
            if (!isEmpty(name.trim())) {
                name += "/";
            }
            name += vendor.name;
        }
        if (!isEmpty(masterCategory)) {
            if (!isEmpty(name.trim())) {
                name += ": ";
            }
            name = masterCategory?.name;
        }
        if (!isEmpty(category)) {
            if (!isEmpty(name.trim())) {
                name += " >> ";
            }
            name += category.name;
        }
        if (!isEmpty(strain) && !isEmpty(strain.name)) {
            if (!isEmpty(name.trim())) {
                name += " - ";
            }
            name += `(${strain.name}) `;
        }

        if (!isEmpty(product)) {
            if (!isEmpty(name.trim()) && !isEmpty(product.sku)) {
                name += "- ";
                name += product.sku;
            }
            if (!isEmpty(name.trim()) && !isEmpty(product.name)) {
                name += " - ";
                name += product?.name;
            }

        }
        const destination = "/analytics/organizations/" + selectedOrganizationId + "/integration/" + selectedIntegration + "/product/" + product?.id;
        return <Link to={destination}>{name}</Link>;
    };

    static getUnitPrice = (product, inventory, transaction) => {
        if (transaction?.unit_price) {
            return transaction.unit_price;
        }
        if (inventory?.unit_price) {
            return inventory.unit_price;
        }
        if (product?.price) {
            return product.price;
        }
        return null;
    };

    static getUnitCost = (product, inventory, transaction) => {
        if (transaction?.unit_cost) {
            return transaction.unit_cost;
        }
        if (inventory?.unit_cost) {
            return inventory.unit_cost;
        }
        if (product?.unit_cost) {
            return product.unit_cost;
        }
        return null;
    };

    static getMargin = (product, inventory, transaction) => {
        const unitPrice = DutchieUtils.getUnitPrice(product, inventory, transaction);
        const unitCost = DutchieUtils.getUnitCost(product, inventory, transaction);
        if (unitPrice && unitCost) {
            return (unitPrice - unitCost);
        }
        return null;
    };

    static getQuantityAvailable = (inventory) => {
        if (isEmpty(inventory) || inventory.deleted) {
            return 0;
        }
        return inventory.quantity_available || 0;
    };

    static getRecommendation = (currentProduct, productVelocities, productInventories, productTransactions, products) => {
        const velocityByPid = productVelocities.reduce((acc, vel) => {
            acc[vel.product_id] = vel;
            return acc;
        }, {});
        const inventoryByPid = productInventories.reduce((acc, inv) => {
            acc[inv.product_id] = inv;
            return acc;
        }, {});
        const transactionsByPid = productTransactions.reduce((acc, txn) => {
            acc[txn.product_id] = txn;
            return acc;
        }, {});
        const productByPid = products.reduce((acc, prod) => {
            acc[prod.id] = prod;
            return acc;
        }, {});

        return productVelocities.reduce((acc, curr) => {
            if (curr.product_id === currentProduct.id) {
                return acc; // Skip the primary product
            }
            const candidate = productByPid[curr.product_id];
            const candidateVelocity = velocityByPid[curr.product_id];
            const candidateInventory = inventoryByPid[curr.product_id];
            const candidateTransaction = transactionsByPid[curr.product_id];
            const candidateMargin = DutchieUtils.getMargin(candidate, candidateInventory, candidateTransaction);

            const candidateScore = parseFloat((candidateVelocity?.velocity || "1.")) * (candidateMargin || 1);
            if (candidateScore > acc.score) {
                return {
                    product_id: curr.product_id, score: candidateScore
                }; // Found a better recommendation
            }
            return acc; // Keep the current best recommendation

        }, {product_id: null, score: -Infinity});
    };

    static getActiveTimeRange = ({
        client,
        productId,
        defaultStart,
        defaultMiddle,
        defaultEnd,
        abortController
    }) => {
        if (isEmpty(productId)) {
            return Promise.resolve([defaultStart, defaultMiddle, defaultEnd]);
        }
        const common = {
            product_id: productId,
            limit: 1,
        };
        return Promise.all([
            Item.fetch({
                client,
                params: {...common, order_by: "transaction__transaction_date"},
                paginate: false,
                cache: true,
                abortController: abortController
            }),
            Item.fetch({
                client,
                params: {...common, order_by: "-transaction__transaction_date"},
                paginate: false,
                cache: true,
                abortController: abortController
            })
        ]).then((firstAndLastItems) => {

            const [firstItem, lastItem] = firstAndLastItems;

            if (isEmpty(firstItem) && isEmpty(lastItem)) {
                return [defaultStart, defaultMiddle, defaultEnd];
            }

            const firstTxnDate = firstItem[0]?.transaction?.transaction_date;
            const lastTxnDate = lastItem[0]?.transaction?.transaction_date;
            const promises = [];

            if (isEmpty(firstItem[0]?.transaction)) {
                promises.push(Transaction.fetchById(client, firstItem[0].transaction_id, abortController));
            } else {
                promises.push(Promise.resolve([firstItem[0].transaction]));
            }

            if (isEmpty(lastItem[0]?.transaction)) {
                promises.push(Transaction.fetchById(client, lastItem[0].transaction_id, abortController));
            } else {
                promises.push(Promise.resolve([lastItem[0].transaction]));
            }

            return Promise.all(promises).then(([firstTransaction, lastTransaction]) => {
                const firstTransactionDate = firstTransaction[0]?.transaction_date;
                const lastTransactionDate = lastTransaction[0]?.transaction_date;
                return [
                    dayjs(firstTransactionDate || defaultStart),
                    dayjs(lastTransactionDate || defaultEnd)
                ];
            })
        }).then(([start, end]) => {
            start = start || dayjs(new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 14));
            end = end || dayjs(new Date());
            if (end.diff(start, "day") > MAX_DAYS || end.diff(start, "day") < MIN_DAYS) {
                start = end.subtract(MAX_DAYS, "day");
            }
            const middle = start.add(end.diff(start) / 2, "ms").toDate();
            start = start.toDate();
            end = end.toDate();

            start.setHours(0);
            start.setMinutes(0);
            start.setSeconds(0);
            start.setMilliseconds(0);

            middle.setHours(0);
            middle.setMinutes(0);
            middle.setSeconds(0);
            middle.setMilliseconds(0);

            end.setHours(0);
            end.setMinutes(0);
            end.setSeconds(0);
            end.setMilliseconds(0);

            console.log("Using active time range: ",
                TimeDelta.formatShortDate(start),
                TimeDelta.formatShortDate(middle),
                TimeDelta.formatShortDate(end)
            );

            return [
                dayjs(start),
                dayjs(middle),
                dayjs(end)
            ];
        });
    };

    static getTransactionsOverRangeForStores = ({
        client,
        startDate,
        endDate,
        abortControllerRef,
        integrationAccountIds,
        setTotal,
        setIsLoading,
        setInProgress
    }) => {

        const feedbackWrapper = DutchieUtils.getFeedbackWrapper(setInProgress);

        // Query for the product baseline at the store in which it operated:
        const common = {
            client,
            startDate: startDate?.toDate ? startDate.toDate() : startDate,
            endDate: endDate?.toDate ? endDate.toDate() : endDate,
            abortController: abortControllerRef.current,
            dutchie_account_id__in: integrationAccountIds.join(","),
            cache: true
        }

        const promises = [
            Employee.fetchByDate({...common}),
            Customer.fetchByDate({...common}),
            Brand.fetchByDate({...common}),
            MasterCategory.fetchByDate({...common}),
            Category.fetchByDate({...common}),
            Strain.fetchByDate({...common}),
            Vendor.fetchByDate({...common}),
            Producer.fetchByDate({...common}),
            Product.fetchByDate({...common}),
            client.dutchie.api.v1().get("/fast-inventory/", {
                params: {
                    dutchie_account_id__in: integrationAccountIds.join(","),
                    proj: Inventory.columns.join(","),
                    start_date: common.startDate.toISOString(),
                    end_date: common.endDate.toISOString(),
                },
                abortController: abortControllerRef.current
            }).then(response => {
                return response?.data?.results || [];
            }),
            Item.fetchByDate({...common}),
            Transaction.fetchByDate({...common}),
            Package.fetchByDate({...common}),
        ].map(promise => {
            return feedbackWrapper(promise);
        });

        setTotal(prev => prev + promises.length);
        setInProgress(prev => prev + promises.length);
        setIsLoading(true);

        return Promise.all(promises).then(([
            fetchedEmployees,
            fetchedCustomers,
            fetchedBrands,
            fetchedMasterCategories,
            fetchedCategories,
            fetchedStrains,
            fetchedVendors,
            fetchedProducers,
            fetchedProducts,
            fetchedInventory,
            fetchedItems,
            fetchedTransactions,
            fetchedPackages,
        ]) => {
            return Model.canonicalize().then(() => {
                return {
                    product: undefined,
                    employees: fetchedEmployees.map(e => CanonicalEmployee.byId.get(e.id) || e.canonical?.[CanonicalEmployee.name]),
                    customers: fetchedCustomers.map(c => CanonicalCustomer.byId.get(c.id) || c.canonical?.[CanonicalCustomer.name]),
                    brands: fetchedBrands.map(b => CanonicalBrand.byId.get(b.id) || b.canonical?.[CanonicalBrand.name]),
                    masterCategories: fetchedMasterCategories.map(mc => CanonicalMasterCategory.byId.get(mc.id) || mc.canonical?.[CanonicalMasterCategory.name]),
                    categories: fetchedCategories.map(c => CanonicalCategory.byId.get(c.id) || c.canonical?.[CanonicalCategory.name]),
                    strains: fetchedStrains.map(s => CanonicalStrain.byId.get(s.id) || s.canonical?.[CanonicalStrain.name]),
                    vendors: fetchedVendors.map(v => CanonicalVendor.byId.get(v.id) || v.canonical?.[CanonicalVendor.name]),
                    producers: fetchedProducers.map(p => CanonicalProducer.byId.get(p.id) || p.canonical?.[CanonicalProducer.name]),
                    products: fetchedProducts.map(p => CanonicalProduct.byId.get(p.id) || p.canonical?.[CanonicalProduct.name]),
                    inventory: fetchedInventory.map(i => CanonicalInventory.byId.get(i.id) || i.canonical?.[CanonicalInventory.name]),
                    items: fetchedItems.map(i => CanonicalTransactionItem.byId.get(i.id) || i.canonical?.[CanonicalTransactionItem.name]),
                    transactions: fetchedTransactions.map(t => CanonicalTransaction.byId.get(t.id) || t.canonical?.[CanonicalTransaction.name]),
                }
            });

        });
    }

    static getTransactionsForProduct = ({
        client,
        abortControllerRef,
        productId,
        startDate,
        endDate,
        setInProgress,
        setIsLoading,
        setTotal,
    }) => {
        const feedbackWrapper = DutchieUtils.getFeedbackWrapper(setInProgress);

        return Product.fetchById(client, productId, abortControllerRef.current).then(fetchedProduct => {
            const product = fetchedProduct[0];
            const dutchieAccountId = product?.dutchie_account_id;
            const getParams = (model) => {
                const productField = model.productIdField;
                let didField = "dutchie_account_id";
                if (productField.substr(-4) === "__id") {
                    didField = productField.substr(0, productField.length - 4) + "__dutchie_account_id";
                } else if (productField.substr(-3) === "_id") {
                    didField = productField.substr(0, productField.length - 3) + "__dutchie_account_id";
                }
                return {
                    client,
                    params: {
                        flat: true,
                        proj: model.columns.join(","),
                        [model.dateField + "__gte"]: startDate,
                        [model.dateField + "__lte"]: endDate,
                        [model.productIdField]: productId,
                        [didField]: dutchieAccountId,
                        dutchie_account_id: dutchieAccountId,
                    },
                    paginate: false,
                    cache: true,
                    abortController: abortControllerRef.current
                }
            }
            console.log("Querying for products with params: ", getParams(Product));
            const promises = [
                Employee.fetch(getParams(Employee)),
                Customer.fetch(getParams(Customer)),
                Brand.fetch(getParams(Brand)),
                MasterCategory.fetch(getParams(MasterCategory)),
                Category.fetch(getParams(Category)),
                Strain.fetch(getParams(Strain)),
                Vendor.fetch(getParams(Vendor)),
                Producer.fetch(getParams(Producer)),
                Inventory.fetch(getParams(Inventory)),
                Item.fetch(getParams(Item)),
                Transaction.fetch(getParams(Transaction)),
                Package.fetch(getParams(Package)),
            ].map(promise => {
                return feedbackWrapper(promise);
            });

            setTotal(prev => prev + promises.length);
            setInProgress(prev => prev + promises.length);
            setIsLoading(true);

            return Promise.all(promises).then(([
                fetchedEmployees,
                fetchedCustomers,
                fetchedBrands,
                fetchedMasterCategories,
                fetchedCategories,
                fetchedStrains,
                fetchedVendors,
                fetchedProducers,
                fetchedInventory,
                fetchedItems,
                fetchedTransactions,
                fetchedPackages,
            ]) => {
                return Model.canonicalize().then(() => {
                    return {
                        product: CanonicalProduct.byId.get(fetchedProduct[0].id),
                        employees: fetchedEmployees.map(e => CanonicalEmployee.byId.get(e.id) || e.canonical),
                        customers: fetchedCustomers.map(c => CanonicalCustomer.byId.get(c.id) || c.canonical),
                        brands: fetchedBrands.map(b => CanonicalBrand.byId.get(b.id) || b.canonical),
                        masterCategories: fetchedMasterCategories.map(mc => CanonicalMasterCategory.byId.get(mc.id) || mc.canonical),
                        categories: fetchedCategories.map(c => CanonicalCategory.byId.get(c.id) || c.canonical),
                        strains: fetchedStrains.map(s => CanonicalStrain.byId.get(s.id) || s.canonical),
                        vendors: fetchedVendors.map(v => CanonicalVendor.byId.get(v.id) || v.canonical),
                        producers: fetchedProducers.map(p => CanonicalProducer.byId.get(p.id) || p.canonical),
                        products: [fetchedProduct].map(p => CanonicalProduct.byId.get(p.id) || p.canonical),
                        inventory: fetchedInventory.map(i => CanonicalInventory.byId.get(i.id) || i.canonical),
                        items: fetchedItems.map(i => CanonicalTransactionItem.byId.get(i.id) || i.canonical),
                        transactions: fetchedTransactions.map(t => CanonicalTransaction.byId.get(t.id) || t.canonical),
                    }
                });

            });
        });
    }

    static getTransactionsOverRange = ({
        client,
        productId,
        startDate,
        endDate,
        abortControllerRef,
        integrationAccountIds,
        setTotal,
        setIsLoading,
        setInProgress
    }) => {
        console.log("Getting transactions over date range: ", startDate, endDate);
        return Promise.all([
            this.getTransactionsOverRangeForStores({
                client,
                startDate,
                endDate,
                abortControllerRef,
                integrationAccountIds,
                setTotal,
                setIsLoading,
                setInProgress
            }),
            this.getTransactionsForProduct({
                client,
                abortControllerRef,
                productId,
                startDate,
                endDate,
                setInProgress,
                setIsLoading,
                setTotal,
            })
        ]).then(([storeData, productData]) => {
            setIsLoading(false);
            setInProgress(0);
            return {
                product: productData.product,
                employees: uniqueById([...storeData.employees, ...productData.employees]),
                customers: uniqueById([...storeData.customers, ...productData.customers]),
                brands: uniqueById([...storeData.brands, ...productData.brands]),
                masterCategories: uniqueById([...storeData.masterCategories, ...productData.masterCategories]),
                categories: uniqueById([...storeData.categories, ...productData.categories]),
                strains: uniqueById([...storeData.strains, ...productData.strains]),
                vendors: uniqueById([...storeData.vendors, ...productData.vendors]),
                producers: uniqueById([...storeData.producers, ...productData.producers]),
                products: uniqueById([...storeData.products, ...productData.products]),
                inventory: uniqueById([...storeData.inventory, ...productData.inventory]),
                items: uniqueById([...storeData.items, ...productData.items]),
                transactions: uniqueById([...storeData.transactions, ...productData.transactions]),
            }
        }).catch(err => {
            console.log("Error getting transactions over range");
            console.error(err);
            setIsLoading(false);
            setInProgress(0);
            return Promise.reject(err);
        });
    }

    static
        getTransactionsForProductForWindow = ({
            client,
            productId,
            activeWindow,
            abortControllerRef,
            integrationAccountIds,
            setTotal,
            setIsLoading,
            setInProgress
        }) => {
            const common = {
                client,
                productId,
                abortControllerRef,
                integrationAccountIds,
                setTotal,
                setIsLoading,
                setInProgress
            }
            return Promise.all([
                DutchieUtils.getTransactionsOverRange({
                    ...common,
                    startDate: activeWindow.previous.start.toDate(),
                    endDate: activeWindow.previous.end.toDate(),
                }),
                DutchieUtils.getTransactionsOverRange({
                    ...common,
                    startDate: activeWindow.current.start.toDate(),
                    endDate: activeWindow.current.end.toDate(),
                })
            ]).then(([previousData, currentData]) => {
                const {
                    product,
                    items: previousFetchedItems,
                    transactions: previousFetchedTransactions,
                    inventory: previousFetchedInventory
                } = previousData;

                const {
                    items: currentFetchedItems,
                    transactions: currentFetchedTransactions,
                    inventory: fetchedInventory
                } = currentData;

                return {
                    product: product,
                    items: uniqueById([...previousFetchedItems, ...currentFetchedItems]),
                    transactions: uniqueById([...previousFetchedTransactions, ...currentFetchedTransactions]),
                    inventory: uniqueById([...previousFetchedInventory, ...fetchedInventory]),
                }
            }).catch(err => {
                console.log("Error getting transactions for product for window");
                console.error(err);
                setIsLoading(false);
                setInProgress(0);
                return Promise.reject(err);
            });
        }
}

export class MetrcUtils extends IntegrationUtils {

    static getActiveTimeRange = ({
        client,
        productId,
        defaultStart,
        defaultMiddle,
        defaultEnd,
        abortController
    }) => {
        if (isEmpty(productId)) {
            return Promise.resolve([defaultStart, defaultMiddle, defaultEnd]);
        }
        const common = {
            transactions__products__id: productId,
            limit: 1
        };
        return Promise.all([
            MetrcReceipt.fetch({
                client,
                params: {...common, order_by: "sales_date_time", limit: 1},
                paginate: false,
                cache: true,
                abortController: abortController
            }),
            MetrcReceipt.fetch({
                client,
                params: {...common, order_by: "-sales_date_time", limit: 1},
                paginate: false,
                cache: true,
                abortController: abortController
            }),
        ]).then((firstAndLastItems) => {
            const [firstItem, lastItem] = firstAndLastItems;

            if (isEmpty(firstItem) && isEmpty(lastItem)) {
                return [defaultStart, defaultMiddle, defaultEnd];
            }

            const firstTxnDate = firstItem[0]?.sales_date_time;
            const lastTxnDate = lastItem[0]?.sales_date_time;

            return [dayjs(firstTxnDate || defaultStart), dayjs(lastTxnDate || defaultEnd)];
        }).then(([start, end]) => {
            start = start || dayjs(new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - MIN_DAYS));
            end = end || dayjs(new Date());
            if (end.diff(start, "day") > MAX_DAYS || end.diff(start, "day") < MIN_DAYS) {
                start = end.subtract(MAX_DAYS, "day");
            }
            const middle = start.add(end.diff(start) / 2, "ms").toDate();
            start = start.toDate();
            end = end.toDate();

            start.setHours(0);
            start.setMinutes(0);
            start.setSeconds(0);
            start.setMilliseconds(0);

            middle.setHours(0);
            middle.setMinutes(0);
            middle.setSeconds(0);
            middle.setMilliseconds(0);

            end.setHours(0);
            end.setMinutes(0);
            end.setSeconds(0);
            end.setMilliseconds(0);

            console.log("Using active time range: ",
                TimeDelta.formatShortDate(start),
                TimeDelta.formatShortDate(middle),
                TimeDelta.formatShortDate(end)
            );

            return [
                dayjs(start),
                dayjs(middle),
                dayjs(end)
            ];
        });
    };

    static getTransactionsOverRange = ({
        client,
        productId,
        startDate,
        endDate,
        abortControllerRef,
        integrationAccountIds,
        setTotal,
        setIsLoading,
        setInProgress
    }) => {

        const common = {
            client,
            startDate: startDate?.toDate ? startDate.toDate() : startDate,
            endDate: endDate?.toDate ? endDate.toDate() : endDate,
            abortController: abortControllerRef.current,
            selectedIntegrationAccounts: integrationAccountIds.map(a => ({id: a})),
            cache: true,
        }

        const feedbackWrapper = DutchieUtils.getFeedbackWrapper(setInProgress);
        const accountIds = integrationAccountIds.join(",");
        const commonFetch = {
            client,
            params: {metrc_account_id__in: accountIds},
            paginate: false,
            cache: true,
            abortController: abortControllerRef.current
        };

        const getParams = (model) => {
            return {
                ...commonFetch,
                params: {
                    ...commonFetch.params,
                    flat: true,
                    proj: model.columns.join(","),
                    limit: 100000000
                },
                cache: true
            }
        }
        const relatedProductModels = [
            MetrcProductCategoryType,
            MetrcProductCategory,
            MetrcProduct,
            MetrcPackage,
            MetrcReceipt,
            MetrcTransaction,
            // MetrcTax,
            MetrcInventory,
            MetrcTransactionItem
        ];


        const byDate = {
            ...common
        }
        // delete byDate.selectedIntegrationAccounts
        console.log("Common params: ", byDate);

        const promises = [
            MetrcFacility.fetch({...getParams(MetrcFacility)}),
            MetrcLocationType.fetch({...getParams(MetrcLocationType)}),
            MetrcLocation.fetch({...getParams(MetrcLocation)}),
            MetrcPackage.fetchByDate({...byDate}),
            MetrcPackageWholesalePrice.fetchByDate({...byDate}),
            MetrcTransaction.fetchByDate({...byDate}),
            MetrcInventory.fetchByDate({...byDate}),
            MetrcProduct.fetchByDate({...byDate}),
            MetrcEmployee.fetchByDate({...byDate}),
            MetrcBrand.fetchByDate({...byDate}),
            MetrcProductCategoryType.fetchByDate({...byDate}),
            MetrcProductCategory.fetch({
                ...common,
                params: {...common.params, proj: MetrcProductCategory.columns.join(","), limit: 10000000, flat: true}
            }),
            MetrcStrain.fetchByDate({...byDate}),
            MetrcReceipt.fetchByDate({...byDate}),
            MetrcTransaction.fetchByDate({...byDate}),
            MetrcTransactionItem.fetchByDate({...byDate}),
            MetrcProduct.fetchById(client, productId, abortControllerRef.current),
            ...relatedProductModels.filter(model => !isEmpty(model.productIdField)).map(model => {
                return model.fetch({
                    client,
                    params: {
                        metrc_account_id__in: accountIds,
                        [model.productIdField]: productId,
                        proj: model.columns.join(","),
                        flat: true,
                        limit: 10000000
                    },
                    abortController: abortControllerRef.current,
                    cache: true,
                    paginate: false
                })
            }),
            // MetrcTax.fetchByDate({...byDate})
        ].map(promise => {
            return feedbackWrapper(promise);
        });

        setTotal(prev => prev + promises.length);
        setInProgress(prev => prev + promises.length);
        setIsLoading(true);

        return Promise.all(promises).then(responses => {
            const responsesByModel = responses.reduce((acc, response) => {
                if (!isEmpty(response) && response.length > 0) {
                    const modelName = response[0].constructor.name;
                    if (!acc[modelName]) {
                        acc[modelName] = {canonicalClasses: response[0].constructor.canonicalClasses, data: []};
                    }
                    acc[modelName].data = [...acc[modelName].data, ...response];
                }
                return acc;
            }, {});

            return Model.canonicalize().then(() => {
                return {
                    product: CanonicalProduct.byId.get(productId),
                    employees: responsesByModel[MetrcEmployee.name]?.data.map(e => CanonicalEmployee.byId.get(e.id)),
                    customers: [],
                    brands: responsesByModel[MetrcBrand.name]?.data.map(b => CanonicalBrand.byId.get(b.id)),
                    masterCategories: responsesByModel[MetrcProductCategoryType.name]?.data.map(mc => CanonicalMasterCategory.byId.get(mc.id)),
                    categories: responsesByModel[MetrcProductCategory.name]?.data.map(c => CanonicalCategory.byId.get(c.id)),
                    strains: responsesByModel[MetrcStrain.name]?.data.map(s => CanonicalStrain.byId.get(s.id)),
                    vendors: [],
                    producers: [],
                    products: responsesByModel[MetrcProduct.name].data?.map(p => CanonicalProduct.byId.get(p.id)),
                    inventory: responsesByModel[MetrcInventory.name].data?.map(i => CanonicalInventory.byId.get(i.id)),
                    items: responsesByModel[MetrcTransactionItem.name].data?.map(i => {
                        const item = CanonicalTransactionItem.byId?.get(i.id);
                        if (!item) {
                            if (Math.random() > .9999) {
                                console.warn("Missing canonical item for metrc transaction item id ", i.id, i);
                            }
                            return null;
                        }
                        return item;
                    }).filter(i => i !== null),
                    transactions: responsesByModel[MetrcTransaction.name].data?.map(t => CanonicalTransaction.byId?.get(t.id)).filter(t => t !== null) || [],
                }
            }).catch(err => {
                console.error("Error during canonicalization of Metrc data", err);
                return Promise.reject(err);
            });
        });
    }

    static getTransactionsForProductForWindow = ({
        client,
        productId,
        activeWindow,
        abortControllerRef,
        integrationAccountIds,
        setTotal,
        setIsLoading,
        setInProgress
    }) => {
        const common = {
            client,
            productId,
            abortControllerRef,
            integrationAccountIds,
            setTotal,
            setIsLoading,
            setInProgress
        }
        return Promise.all([
            MetrcUtils.getTransactionsOverRange({
                ...common,
                startDate: activeWindow.previous.start.toDate(),
                endDate: activeWindow.previous.end.toDate(),
            }),
            MetrcUtils.getTransactionsOverRange({
                ...common,
                startDate: activeWindow.current.start.toDate(),
                endDate: activeWindow.current.end.toDate(),
            })
        ]).then(([previousData, currentData]) => {
            const {
                product,
                items: previousFetchedItems,
                transactions: previousFetchedTransactions,
                inventory: previousFetchedInventory
            } = previousData;

            const {
                items: currentFetchedItems,
                transactions: currentFetchedTransactions,
                inventory: fetchedInventory
            } = currentData;

            return {
                product: product,
                items: uniqueById([...previousFetchedItems, ...currentFetchedItems]),
                transactions: uniqueById([...previousFetchedTransactions, ...currentFetchedTransactions]),
                inventory: uniqueById([...previousFetchedInventory, ...fetchedInventory]),
            }
        }).catch(err => {
            console.error(err);
            setIsLoading(false);
            setInProgress(0);
            return Promise.reject(err);
        });
    }

}